import { useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Link2, MessageSquare, BookOpen, CalendarClock, Wand2, Tag, User, Store, Zap,
  AlertTriangle, ShieldAlert, XCircle, Phone, Loader2, Upload, FileAudio, RefreshCw,
  Mic, Square,
} from "lucide-react";
import { smsApi, reais } from "./api";
import { MODELOS } from "./modelos";
import { sanear, descreverTrocas } from "./texto";
import PreviewSMS from "./PreviewSMS";
import Destinatarios from "./Destinatarios";
import AgendarPicker, { hojeIso } from "./AgendarPicker";
import Dialogo from "./Dialogo";

const LIMITE_SMS = 160;
// O link ocupa 24 caracteres + 1 da quebra de linha que o separa da ultima
// palavra — total 25 reservados. A quebra e' o que garante que o link nunca
// sai colado no texto.
const LINK_CHARS = 24;
const RESERVA_LINK = LINK_CHARS + 1;
const LINK_PREVIA = "link.do.seu.cardapio.com"; // 24 chars, igual ao link real
// Instrucao de descadastro em TODA mensagem: 18 chars + 1 da quebra = 19.
// Quem anexa de verdade e' o backend; aqui so' contamos e mostramos na previa.
const PARE_TEXTO = "Envie PARE p/ sair";
const RESERVA_PARE = PARE_TEXTO.length + 1;
// {nome} reserva o pior caso: primeiro nome cortado em 15 no envio.
const NOME_RESERVA = 15;

export default function NovaCampanhaSMS({ onConcluida, canal = "sms" }) {
  const ehVoz = canal === "voz";
  const [segmentos, setSegmentos] = useState([]);
  const [publico, setPublico] = useState([]);
  const [bloqueados, setBloqueados] = useState([]);
  const [empresa, setEmpresa] = useState("");
  const [selecionados, setSelecionados] = useState(new Set());
  const [avulsos, setAvulsos] = useState([]);
  const [consentimento, setConsentimento] = useState(false);
  const [mostrarModelos, setMostrarModelos] = useState(false);
  const [nome, setNome] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [linkAtivo, setLinkAtivo] = useState(true);
  const [trocas, setTrocas] = useState([]);
  const [quando, setQuando] = useState("agora"); // agora | agendar
  const [dataAg, setDataAg] = useState(hojeIso());
  const [horaAg, setHoraAg] = useState("10");
  const [minAg, setMinAg] = useState("00");
  const [orcamento, setOrcamento] = useState(null);
  // MP3 que a loja subiu: { url, nome } — a mesma URL que a SMSMais baixa.
  const [audioVoz, setAudioVoz] = useState(null);
  const [enviandoAudio, setEnviandoAudio] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [segGravados, setSegGravados] = useState(0);
  const gravadorRef = useRef(null); // { rec, stream, timer } da gravacao ativa
  const [enviando, setEnviando] = useState(false);
  // Dialogo do sistema aberto: "consentimento" | "semlink" | "confirmar" | {erro}
  const [dialogo, setDialogo] = useState(null);

  useEffect(() => {
    smsApi.segmentos().then((d) => setSegmentos(d.segmentos)).catch(() => {});
    smsApi.publico().then((d) => {
      setPublico(d.destinatarios);
      setBloqueados(d.bloqueados || []);
      setEmpresa(d.empresa || "");
    }).catch(() => setPublico([]));
  }, []);

  // Contagem no PIOR caso: {empresa} pelo tamanho real do nome da loja,
  // {nome} pelos 15 reservados, link + quebra quando ativo.
  const expandirPiorCaso = (t) =>
    (t || "").split("{empresa}").join(empresa).split("{nome}").join("X".repeat(NOME_RESERVA));
  const usados = expandirPiorCaso(mensagem).length + (linkAtivo ? RESERVA_LINK : 0) + RESERVA_PARE;

  const aoDigitar = (valor) => {
    const r = sanear(valor);
    let texto = r.texto;
    // Recusa o excedente ja' expandido: colar um textao corta no que cabe.
    while (texto && expandirPiorCaso(texto).length + (linkAtivo ? RESERVA_LINK : 0) + RESERVA_PARE > LIMITE_SMS) {
      texto = texto.slice(0, -1);
    }
    setMensagem(texto);
    if (r.mudou) {
      setTrocas((atual) => {
        const mapa = new Map(atual.map((t) => [t.de, t.para]));
        r.trocados.forEach((t) => mapa.set(t.de, t.para));
        return [...mapa.entries()].map(([de, para]) => ({ de, para }));
      });
    }
  };

  const inserirToken = (token) => aoDigitar(`${mensagem}${mensagem.endsWith(" ") || !mensagem ? "" : " "}${token} `);

  const enviarAudio = async (arquivo) => {
    setEnviandoAudio(true);
    try {
      const r = await smsApi.subirAudioVoz(arquivo);
      setAudioVoz({ url: r.url, nome: arquivo.name, duracao: r.duracao_segundos });
      // `mensagem` vira o rotulo no historico e destrava o orcamento.
      setMensagem(`Áudio: ${arquivo.name}`);
    } catch (err) {
      setDialogo({ erro: err?.response?.data?.detail || "Não foi possível enviar o áudio." });
    } finally {
      setEnviandoAudio(false);
    }
  };

  const aoEscolherAudio = (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ""; // permite reescolher o mesmo arquivo
    if (arquivo) enviarAudio(arquivo);
  };

  const pararGravacao = () => {
    const g = gravadorRef.current;
    if (g?.rec && g.rec.state !== "inactive") g.rec.stop();
  };

  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const pedacos = [];
      rec.ondataavailable = (e) => { if (e.data.size) pedacos.push(e.data); };
      rec.onstop = () => {
        clearInterval(gravadorRef.current?.timer);
        stream.getTracks().forEach((t) => t.stop());
        setGravando(false);
        const blob = new Blob(pedacos, { type: rec.mimeType || "audio/webm" });
        // O backend converte webm/opus pra MP3 — mesmo funil do upload.
        enviarAudio(new File([blob], "gravacao-ao-vivo.webm", { type: blob.type }));
      };
      gravadorRef.current = { rec, stream, timer: null };
      setSegGravados(0);
      setGravando(true);
      rec.start();
      const inicio = Date.now();
      gravadorRef.current.timer = setInterval(() => {
        const seg = Math.floor((Date.now() - inicio) / 1000);
        setSegGravados(seg);
        if (seg >= 29) pararGravacao(); // teto da operadora: corta aos 30s
      }, 250);
    } catch {
      setDialogo({ erro: "Não foi possível acessar o microfone — libere a permissão no navegador." });
    }
  };

  // Popup fechado no meio da gravacao: desliga o microfone.
  useEffect(() => () => {
    const g = gravadorRef.current;
    clearInterval(g?.timer);
    g?.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  const idsSelecionados = useMemo(() => [...selecionados], [selecionados]);
  const textoFinal = useMemo(
    () => (!ehVoz && linkAtivo && mensagem ? `${mensagem}\n{link}` : mensagem),
    [mensagem, linkAtivo, ehVoz]
  );

  // Previa mostra os atalhos como {NOME} e {EMPRESA} (pedido do dono): deixa
  // visivel ONDE cada um entra — o valor muda por destinatario no envio. O
  // link aparece em linha propria com o dominio de exemplo.
  const previa = useMemo(() => {
    let t = mensagem.split("{empresa}").join("{EMPRESA}").split("{nome}").join("{NOME}");
    if (linkAtivo && t) t += `\n${LINK_PREVIA}`;
    if (t) t += `\n${PARE_TEXTO}`;
    return t;
  }, [mensagem, linkAtivo]);

  useEffect(() => {
    if (!idsSelecionados.length && !avulsos.length) { setOrcamento(null); return; }
    const t = setTimeout(() => {
      smsApi.orcar({
        canal, cliente_ids: idsSelecionados, telefones_avulsos: avulsos, mensagem: textoFinal,
      }).then(setOrcamento).catch(() => setOrcamento(null));
    }, 300);
    return () => clearTimeout(t);
  }, [idsSelecionados, avulsos, textoFinal]);

  const agendadaPara = quando === "agendar" ? `${dataAg} ${horaAg}:${minAg}` : null;

  const disparar = () => {
    if (ehVoz && !audioVoz) { setDialogo({ erro: "Envie o áudio da ligação antes de disparar." }); return; }
    if (avulsos.length && !consentimento) { setDialogo("consentimento"); return; }
    if (!ehVoz && !linkAtivo) { setDialogo("semlink"); return; }
    setDialogo("confirmar");
  };

  const enviarDeFato = async () => {
    setDialogo(null);
    setEnviando(true);
    try {
      const c = await smsApi.criar({
        canal,
        audio_url: ehVoz ? audioVoz?.url : undefined,
        nome: nome || (ehVoz ? "Campanha de Ligação" : "Campanha de SMS"), mensagem: textoFinal,
        cliente_ids: idsSelecionados, telefones_avulsos: avulsos,
        consentimento_confirmado: consentimento,
        agendada_para: agendadaPara,
      });
      const r = await smsApi.disparar(c.id);
      // Sucesso vira toast de 5s no PainelSMS — nada de alert do navegador.
      onConcluida?.(c.id, agendadaPara
        ? `Campanha agendada para ${dataAg.split("-").reverse().join("/")} às ${horaAg}:${minAg} — ${r.enviados} pessoa${r.enviados === 1 ? " vai" : "s vão"} receber.`
        : `Campanha enviada para ${r.enviados} pessoa${r.enviados === 1 ? "" : "s"}.`);
    } catch (e) {
      setDialogo({ erro: e?.response?.data?.detail || "Não foi possível disparar a campanha." });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Sem items-start: as duas colunas esticam juntas e o card da
            Mensagem absorve a diferenca no textarea — fundos alinhados com a
            Previa, sem espaco morto. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 flex flex-col gap-5">
          {/* ------------------------------------- nome + quando enviar */}
          <div className="rounded-xl border bg-card p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary" /> Nome da campanha
                </label>
                <input value={nome} onChange={(e) => setNome(e.target.value)} autoFocus
                  placeholder="Ex: Promoção de terça"
                  className="mt-2 w-full rounded-lg border bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-primary" /> Quando enviar
                </label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="flex gap-1 rounded-full border p-0.5">
                    <button onClick={() => setQuando("agora")}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        quando === "agora" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                      <Zap className="w-3 h-3" /> Agora
                    </button>
                    <button onClick={() => setQuando("agendar")}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        quando === "agendar" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                      <CalendarClock className="w-3 h-3" /> Agendar
                    </button>
                  </div>
                  {quando === "agendar" && (
                    <AgendarPicker
                      dataAg={dataAg} setDataAg={setDataAg}
                      horaAg={horaAg} setHoraAg={setHoraAg}
                      minAg={minAg} setMinAg={setMinAg}
                    />
                  )}
                </div>
                {quando === "agendar" && (
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Só entre 9h e 21h45 — fora da janela legal o envio é recusado.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ---------------------------------------------------- mensagem */}
          <div className="rounded-xl border bg-card p-5 flex-1 flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-primary/10 text-primary p-2">
                  {ehVoz ? <Phone className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                </span>
                <div>
                  <h3 className="font-semibold">{ehVoz ? "Áudio da ligação" : "Mensagem"}</h3>
                  <p className="text-xs text-muted-foreground">
                    {ehVoz ? "Envie o MP3 pronto — é ele que toca quando o cliente atende"
                           : "O contador já desconta o link e os atalhos"}
                  </p>
                </div>
              </div>
              {!ehVoz && <button onClick={() => setMostrarModelos((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium hover:border-primary transition-colors">
                <BookOpen className="w-3.5 h-3.5" /> Modelos
              </button>}
            </div>

            {mostrarModelos && (
              <div className="mb-4 grid gap-2">
                {MODELOS.map((m) => (
                  <button key={m.id}
                    onClick={() => { aoDigitar(m.texto); setMostrarModelos(false); }}
                    className="text-left rounded-lg border p-3 hover:border-primary transition-colors">
                    <p className="text-xs font-semibold">{m.titulo}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.texto}</p>
                  </button>
                ))}
              </div>
            )}

            {ehVoz ? (
              <div className="flex-1 flex flex-col min-h-[140px]">
                <input id="audio-voz-input" type="file"
                  accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.opus,.flac,.amr,.3gp"
                  className="hidden" onChange={aoEscolherAudio} />
                {gravando ? (
                  <div className="flex-1 rounded-lg border-2 border-dashed border-red-500/60 grid place-items-center py-8">
                    <div className="text-center px-4">
                      <span className="relative inline-flex mb-1">
                        <Mic className="w-6 h-6 text-red-500" />
                        <span className="absolute -top-0.5 -right-1.5 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      </span>
                      <p className="text-2xl font-bold tabular-nums">
                        0:{String(segGravados).padStart(2, "0")}
                        <span className="text-sm text-muted-foreground font-medium"> / 0:29</span>
                      </p>
                      <button onClick={pararGravacao} disabled={segGravados < 9}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-500 text-white px-4 py-2 text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        <Square className="w-3.5 h-3.5 fill-current" />
                        {segGravados < 9 ? `Grave mais de 8s (faltam ${9 - segGravados}s)` : "Parar e usar"}
                      </button>
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Mínimo de 8 segundos · para sozinho aos 29s — o teto da operadora.
                      </p>
                    </div>
                  </div>
                ) : !audioVoz ? (
                  <div className="flex-1 rounded-lg border-2 border-dashed grid place-items-center py-8">
                    <div className="text-center px-4">
                      {enviandoAudio ? (<>
                        <Loader2 className="w-6 h-6 mx-auto mb-2 text-primary animate-spin" />
                        <span className="block text-sm font-semibold">Enviando áudio...</span>
                      </>) : (
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <label htmlFor="audio-voz-input"
                            className="inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-semibold cursor-pointer hover:border-primary transition-colors">
                            <Upload className="w-4 h-4 text-primary" /> Enviar arquivo
                          </label>
                          <button onClick={iniciarGravacao}
                            className="inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-semibold hover:border-primary transition-colors">
                            <Mic className="w-4 h-4 text-primary" /> Gravar agora
                          </button>
                        </div>
                      )}
                      <span className="block text-xs text-muted-foreground mt-2">
                        MP3, WAV, M4A, OGG... — convertemos pra MP3 na hora ·
                        mais de 8 e até 29 segundos (a operadora corta aos 30s) · até 8MB
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 rounded-lg border p-4 flex flex-col justify-center gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium flex items-center gap-2 min-w-0">
                        <FileAudio className="w-4 h-4 text-primary shrink-0" />
                        <span className="truncate">{audioVoz.nome}</span>
                        {audioVoz.duracao != null && (
                          <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                            {audioVoz.duracao}s
                          </span>
                        )}
                      </p>
                      <div className="flex gap-2 shrink-0">
                        <label htmlFor="audio-voz-input"
                          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium cursor-pointer hover:border-primary transition-colors">
                          {enviandoAudio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          Trocar áudio
                        </label>
                        <button onClick={iniciarGravacao}
                          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium hover:border-primary transition-colors">
                          <Mic className="w-3.5 h-3.5" /> Gravar de novo
                        </button>
                      </div>
                    </div>
                    <audio controls src={audioVoz.url} className="w-full" />
                    <p className="text-[11px] text-muted-foreground">
                      Ouça antes de enviar — áudio é caro de errar depois que ligou.
                    </p>
                  </div>
                )}
              </div>
            ) : (
            <div className="relative flex-1 min-h-[96px]">
              <textarea value={mensagem} onChange={(e) => aoDigitar(e.target.value)}
                placeholder="Ex: Oi {nome}! Hoje tem 20% OFF na {empresa}. Peca pelo link:"
                className="w-full h-full rounded-lg border bg-background px-3 py-2.5 pr-24 text-sm resize-none" />
              <span className={`absolute right-3 bottom-3 text-xs tabular-nums ${
                usados >= LIMITE_SMS ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                {`${usados} / ${LIMITE_SMS}`}
              </span>
            </div>
            )}

            {!ehVoz && (<>
            {/* atalhos */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={() => inserirToken("{nome}")}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs hover:border-primary transition-colors">
                <User className="w-3 h-3" /> {"{nome}"} <span className="text-muted-foreground">do cliente</span>
              </button>
              <button onClick={() => inserirToken("{empresa}")}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs hover:border-primary transition-colors">
                <Store className="w-3 h-3" /> {"{empresa}"} <span className="text-muted-foreground">{empresa || "sua loja"}</span>
              </button>
              <label className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs cursor-pointer">
                <input type="checkbox" checked={linkAtivo}
                  onChange={(e) => setLinkAtivo(e.target.checked)} className="rounded border-input w-3 h-3" />
                <Link2 className="w-3 h-3 text-primary" /> link da campanha
              </label>
            </div>
            {!linkAtivo && (
              <p className="mt-2 text-xs text-amber-500 flex items-start gap-1.5">
                <Link2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Sem o link, a campanha fica cega: não dá para medir cliques, pedidos nem
                faturamento — o ROI dela vai aparecer zerado no painel.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">
              {"{empresa}"} conta {empresa.length || 10} caracteres · {"{nome}"} reserva {NOME_RESERVA} (nomes
              maiores são cortados; cliente sem nome, o atalho some) · o link usa {RESERVA_LINK} · "{PARE_TEXTO}"
              vai em toda mensagem e usa {RESERVA_PARE}.
            </p>

            {trocas.length > 0 && (
              <p className="mt-2 text-xs text-primary flex items-start gap-1.5">
                <Wand2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Ajustamos caracteres que fariam a mesma mensagem custar 2 a 3 envios:{" "}
                  {descreverTrocas(trocas)}. Acento e emoji não podem ser usados no SMS.
                </span>
              </p>
            )}
            </>)}
          </div>

        </div>

          {ehVoz ? (
            <div className="rounded-xl border bg-card p-5 h-full flex flex-col">
              <h3 className="font-semibold mb-1">Prévia da ligação</h3>
              <p className="text-xs text-muted-foreground mb-4">O que o cliente vai ouvir ao atender</p>
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
                <span className="grid place-items-center w-16 h-16 rounded-full bg-primary/10">
                  <Phone className="w-8 h-8 text-primary" />
                </span>
                {audioVoz ? (
                  <audio controls src={audioVoz.url} className="w-full" />
                ) : (
                  <p className="text-sm text-muted-foreground max-w-[220px]">
                    Envie o MP3 ao lado para ouvir aqui como o cliente vai receber.
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground max-w-[230px]">
                  A mesma gravação toca para todos — sem {"{nome}"}, sem link. Rastreio
                  por cupom falado chega na próxima versão.
                </p>
              </div>
            </div>
          ) : (
            <PreviewSMS texto={previa} remetente={empresa || "Sua loja"} />
          )}
        </div>

        {/* Largura total do popup, abaixo da previa — pedido do dono. */}
        <Destinatarios
          publico={publico} bloqueados={bloqueados} segmentos={segmentos}
          selecionados={selecionados} setSelecionados={setSelecionados}
          avulsos={avulsos} setAvulsos={setAvulsos}
          consentimento={consentimento} setConsentimento={setConsentimento}
          funil={orcamento?.funil}
        />
      </div>


      {dialogo === "consentimento" && (
        <Dialogo icone={ShieldAlert} tom="alerta" titulo="Falta confirmar a autorização"
          onFechar={() => setDialogo(null)}
          acoes={[{ rotulo: "Entendi", primario: true, onClick: () => setDialogo(null) }]}>
          <p className="text-center">
            Você adicionou números de fora da base. Marque a confirmação de autorização
            no bloco Destinatários antes de enviar.
          </p>
        </Dialogo>
      )}

      {dialogo === "semlink" && (
        <Dialogo icone={AlertTriangle} tom="alerta" titulo="Enviar sem o link da campanha?"
          onFechar={() => setDialogo(null)}
          acoes={[
            { rotulo: "Ativar o link", primario: true, onClick: () => { setLinkAtivo(true); setDialogo(null); } },
            { rotulo: "Enviar sem link", onClick: () => setDialogo("confirmar") },
          ]}>
          <p className="mb-2">O link é o que mede o resultado. Sem ele:</p>
          <ul className="space-y-1 list-disc pl-4">
            <li>não dá para saber quem clicou nem quem comprou por causa do SMS;</li>
            <li>pedidos, faturamento e receita por SMS ficam zerados;</li>
            <li>o ROI aparece como "—" e a campanha fica fora do ranking;</li>
            <li>você gasta o mesmo, sem conseguir comparar com as outras.</li>
          </ul>
        </Dialogo>
      )}

      {dialogo === "confirmar" && (
        <Dialogo icone={agendadaPara ? CalendarClock : Send}
          titulo={agendadaPara ? "Agendar esta campanha?" : "Enviar campanha agora?"}
          onFechar={() => setDialogo(null)}
          acoes={[
            { rotulo: "Cancelar", onClick: () => setDialogo(null) },
            { rotulo: agendadaPara ? "Agendar" : "Enviar agora", primario: true,
              desabilitado: enviando, onClick: enviarDeFato },
          ]}>
          <p className="text-center">
            <strong className="text-foreground tabular-nums">{orcamento?.custo.creditos}</strong>{" "}
            pessoa{orcamento?.custo.creditos === 1 ? "" : "s"} ·{" "}
            <strong className="text-foreground tabular-nums">{orcamento?.custo.formatado}</strong>
            {agendadaPara && (
              <>
                <br />
                <span className="tabular-nums">
                  {dataAg.split("-").reverse().join("/")} às {horaAg}:{minAg}
                </span>
              </>
            )}
          </p>
        </Dialogo>
      )}

      {dialogo?.erro && (
        <Dialogo icone={XCircle} tom="erro" titulo="Não deu certo"
          onFechar={() => setDialogo(null)}
          acoes={[{ rotulo: "Fechar", primario: true, onClick: () => setDialogo(null) }]}>
          <p className="text-center">{dialogo.erro}</p>
        </Dialogo>
      )}

      {/* ------------------------------------------------------ barra final */}
      <div className="border-t bg-card px-5 py-3 shrink-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">
              Custo estimado:{" "}
              <span className="font-bold text-foreground text-base">
                {orcamento?.custo.formatado || reais(0)}
              </span>
            </p>
            {orcamento?.impedimento && (
              <p className="text-xs text-destructive mt-0.5">{orcamento.impedimento}</p>
            )}
            {!orcamento && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {ehVoz ? "Selecione os destinatários e envie o áudio." : "Selecione os destinatários e escreva a mensagem."}
              </p>
            )}
          </div>
          <button onClick={disparar} disabled={!orcamento?.pode_disparar || enviando}
            className="rounded-lg bg-primary text-primary-foreground px-6 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-40">
            {quando === "agendar" ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {enviando ? "Enviando..."
              : quando === "agendar" ? "Agendar campanha"
              : orcamento?.pode_disparar ? `${ehVoz ? "Ligar" : "Enviar"} para ${orcamento.custo.creditos}`
              : ehVoz ? "Fazer as ligações" : "Enviar campanha"}
          </button>
        </div>
      </div>
    </div>
  );
}
